import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { STORE_API, type Fen, type GameStore, type PieceType, type Phase, type Square, type Uci } from '@chess/api-model';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { OPENINGS_API, type Openings } from '@chess/api-openings';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { useSelectedGame } from '@chess/api-navigation/react';
import '@chess/api-insights/charts.css';
import { LastGameAggregator, lastGameOf, stageHref, stageStatus } from './LastGameAggregator.ts';
import { LastGameSection, type GameLinks } from './LastGameSection.tsx';
import { moveNames } from './move-names.ts';
import type { LastGame } from './LastGameAggregator.ts';
import { useVerdict } from '@chess/api-verdict/react';
import { DASHBOARD_PARTS } from '@chess/api-dashboard';

// The per-game sections, on Home only (2026-10-09; the stage screens showed
// one each under a game bar before): the selected game (`?game=`, the latest
// without one) stage by stage, under a stage filter (`?stage=`, All by
// default) that only picks stages: each stage shows both its rows, the game
// (result, score through the stage, the stage's own card) and the game
// against the reader's history (against the average, the stage's own
// comparison, over time). The whole section opens that game's review at
// the stage. Game stats shows all three, one under the other. A selected game that
// ended before the stage, or is not scanned yet, says so. One aggregator per
// stage, each registered with the game store here. Never hidden while the
// store loads: the cards show skeletons for their values.

const STAGES: readonly Phase[] = ['opening', 'middlegame', 'ending'];
const FILTER_LABEL: Record<Phase, string> = { opening: 'Opening', middlegame: 'Middlegame', ending: 'Ending' };

/** The stage filter in the address: `?stage=opening`, none for All. */
function useStageFilter(): [Phase | undefined, (stage: Phase | undefined) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('stage');
  const stage = STAGES.includes(raw as Phase) ? (raw as Phase) : undefined;
  const set = (next: Phase | undefined) =>
    setParams((p) => {
      const out = new URLSearchParams(p);
      if (next) out.set('stage', next);
      else out.delete('stage');
      return out;
    });
  return [stage, set];
}

// The filter over the sections: All, or one stage.
const StageFilter: React.FC = () => {
  const [stage, setStage] = useStageFilter();
  const options: Array<{ key: Phase | undefined; label: string }> = [{ key: undefined, label: 'All' }, ...STAGES.map((s) => ({ key: s, label: FILTER_LABEL[s] }))];
  return (
    <div className="last-game-filter" role="tablist" aria-label="Stage">
      {options.map((o) => (
        <button key={o.label} type="button" role="tab" aria-selected={stage === o.key} className={`last-game-filter-option${stage === o.key ? ' last-game-filter-on' : ''}`} onClick={() => setStage(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
};

type Names = Readonly<Record<number, string>>;

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  // The opening variation at each of the reader's opening moves (2026-10-10),
  // for "Your moves" tooltips: the game's record parsed for its first move
  // (which picks the book) and its positions, then the `openings` book names
  // each move's position (`move-names.ts`). Once per game; a failure names nothing.
  const namesCache = new Map<string, Promise<Names>>();
  const namesOf = (game: LastGame): Promise<Names> => {
    const key = `${game.id}:${game.moves?.length ?? 0}`;
    let found = namesCache.get(key);
    if (!found) {
      found = (async (): Promise<Names> => {
        if (!game.moves?.length) return {};
        const [store, chess, openings] = await Promise.all([getApi<GameStore>(STORE_API), getApi<Chess>(CHESS_API), getApi<Openings>(OPENINGS_API)]);
        const record = await store.record(game.id);
        const parsed = record ? chess.parsePgn(record.pgn) : undefined;
        const first = parsed?.moves[0]?.san;
        if (!parsed || !first) return {};
        return moveNames(await openings.load(first), game.moves, parsed.positions);
      })().catch(() => ({}));
      namesCache.set(key, found);
    }
    return found;
  };
  const useMoveNames = (game: LastGame | undefined): Names | undefined => {
    const [names, setNames] = React.useState<{ key: string; names: Names }>();
    const key = game ? `${game.id}:${game.moves?.length ?? 0}` : undefined;
    React.useEffect(() => {
      if (!game || !key) return;
      let live = true;
      void namesOf(game).then((n) => live && setNames({ key, names: n }));
      return () => {
        live = false;
      };
    }, [key]);
    return names && names.key === key ? names.names : undefined;
  };
  // The engine's move in SAN for the habit card, through the chess API once it is there.
  let chess: Chess | undefined;
  void getApi<Chess>(CHESS_API).then((c) => (chess = c));
  const sanOf = (fen: Fen, uci: Uci) =>
    chess?.play(fen, { from: uci.slice(0, 2) as Square, to: uci.slice(2, 4) as Square, promotion: (uci[4] as PieceType | undefined) || undefined })?.move.san;
  // Under the game's card (3), before the stages (10–12).
  app.registerExtension('game.sections', StageFilter, { order: 5 });
  for (const stage of STAGES) {
    const aggregator = new LastGameAggregator(stage);
    registerAggregator(getApi, aggregator);
    const Section: React.FC = () => {
      const navigate = useNavigate();
      const [filter] = useStageFilter();
      const { result: all, state } = useAggregate(getApi, aggregator);
      const selected = useSelectedGame(getApi);
      const verdict = useVerdict(getApi);
      const ready = state?.status === 'ready' && (!filter || filter === stage);
      const names = useMoveNames(stage === 'opening' && ready ? lastGameOf(all, stage, selected.game?.id)?.game : undefined);
      // Another stage chosen: this one steps aside.
      if (filter && filter !== stage) return null;
      if (state?.status !== 'ready') return state?.status === 'failed' ? null : <LastGameSection stage={stage} history loading />;
      const result = lastGameOf(all, stage, selected.game?.id);
      // The result card's caption: the verdict API's sentence on the stage, with the period's average to set it against.
      const caption = result?.game && verdict ? verdict.stage(stage, { ...result.game.facts, average: result.average, averageOf: result.averageOf })?.text : undefined;
      const href = result?.game ? stageHref(result.game) : undefined;
      const reviewPath = result?.game?.reviewPath;
      // A move opens the position before it, where the board draws the move played and the engine's.
      const moveHref = reviewPath ? (ply: number) => `${reviewPath}#ply=${Math.max(0, ply - 1)}` : undefined;
      // Any game's review just before a move of it: the habit card's other games.
      const at = (path: string, ply: number) => `${path}#ply=${Math.max(0, ply - 1)}`;
      const games: GameLinks = { href: at, open: (path, ply) => navigate(at(path, ply)) };
      return (
        <LastGameSection
          stage={stage}
          history
          result={result}
          status={stageStatus(selected.game, stage)}
          verdict={caption}
          href={href}
          onOpen={href ? () => navigate(href) : undefined}
          moveHref={moveHref}
          onOpenMove={moveHref ? (ply) => navigate(moveHref(ply)) : undefined}
          games={games}
          sanOf={sanOf}
          onNavigate={(to) => navigate(to)}
          moveNames={names}
        />
      );
    };
    // On Home, under the filter, in stage order; inside `dashboard.parts`
    // (plugin `shared-dashboard`), which gives the section its rows and its
    // cards their frame (`useDashboardParts`).
    const Framed: React.FC = () => (
      <app.Extension name={DASHBOARD_PARTS}>
        <Section />
      </app.Extension>
    );
    app.registerExtension('game.sections', Framed, { order: 10 + STAGES.indexOf(stage) });
  }
}
